import React, { useEffect, useRef } from 'react';
interface FluidFlowProps {
  from: 'pbs' | 'pathogen' | 'chamber';
  to: 'chamber' | 'waste';
  chamberId: number;
  color: string;
}
export const FluidFlow: React.FC<FluidFlowProps> = ({
  from,
  to,
  chamberId,
  color
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Set canvas dimensions
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    // Animation variables
    let particles: {
      x: number;
      y: number;
      size: number;
      speed: number;
    }[] = [];
    let animationId: number;
    // Define start and end points based on 'from', 'to', and 'chamberId'
    let startX, startY, endX, endY;
    // Calculate chamber positions (2x2 grid in center area)
    const chamberOffsets = [{
      row: 0,
      col: 0
    }, {
      row: 0,
      col: 1
    }, {
      row: 1,
      col: 0
    }, {
      row: 1,
      col: 1
    } // Chamber 4: bottom-right
    ];
    const chamberIndex = chamberId - 1;
    const chamberOffset = chamberOffsets[chamberIndex];
    // Center section starts at 25% and takes 50% of width
    const centerStartX = canvas.width * 0.25;
    const centerWidth = canvas.width * 0.5;
    const chamberWidth = centerWidth / 2;
    const chamberHeight = canvas.height / 2;
    const chamberCenterX = centerStartX + chamberWidth * chamberOffset.col + chamberWidth / 2;
    const chamberCenterY = chamberHeight * chamberOffset.row + chamberHeight / 2;
    if (from === 'pbs' && to === 'chamber') {
      startX = canvas.width * 0.15;
      startY = canvas.height * 0.3;
      endX = chamberCenterX;
      endY = chamberCenterY;
    } else if (from === 'pathogen' && to === 'chamber') {
      startX = canvas.width * 0.15;
      startY = canvas.height * 0.7;
      endX = chamberCenterX;
      endY = chamberCenterY;
    } else if (from === 'chamber' && to === 'waste') {
      startX = chamberCenterX;
      startY = chamberCenterY;
      endX = canvas.width * 0.85;
      endY = canvas.height * 0.5;
    } else {
      startX = 0;
      startY = 0;
      endX = canvas.width;
      endY = canvas.height;
    }
    // Create particles
    for (let i = 0; i < 30; i++) {
      particles.push({
        x: startX,
        y: startY,
        size: Math.random() * 3 + 2,
        speed: Math.random() * 2 + 1
      });
    }
    // Animation function
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Draw path
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
      // Update and draw particles
      particles.forEach(particle => {
        // Calculate direction vector
        const dx = endX - startX;
        const dy = endY - startY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        // Normalize and scale by speed
        const vx = dx / distance * particle.speed;
        const vy = dy / distance * particle.speed;
        // Update position
        particle.x += vx;
        particle.y += vy;
        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        // Reset if reached end
        const distanceToEnd = Math.sqrt(Math.pow(particle.x - endX, 2) + Math.pow(particle.y - endY, 2));
        if (distanceToEnd < 10) {
          particle.x = startX;
          particle.y = startY;
        }
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [from, to, chamberId, color]);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-58" />;
};